import type { Sprint, StatusDef, TicketListItem } from '../../api/types';

/**
 * 🔴 THE RULES OF `POST /sprints/:id/close`, RESTATED IN THE CLIENT — and every one of them read off
 * the API's own `services/sprints.ts`, not invented here.
 *
 * ⭐⭐ WHY THIS FILE IS PURE AND SEPARATE FROM THE DIALOG. Each of these is a claim the UI makes to a
 * user immediately BEFORE an irreversible write: which sprints may receive the carry-over, and how
 * many tickets are about to move. A claim that decides what a user consents to has to be testable on
 * its own, without a DOM.
 *
 * ⛔ NONE OF THIS IS THE ENFORCEMENT. The API re-checks all of it and answers 400/409 with its own
 * wording, which the dialog shows verbatim. This exists so the UI does not OFFER a choice the API
 * will refuse — the same rule the status dropdown follows (SRS FR-2).
 */

// ── Which sprints may receive the unfinished tickets ──────────────────────────────────────────────

/**
 * The legal carry-over destinations, from `resolveCarryOverTarget`'s three refusals:
 *
 *   · a target in ANOTHER PROJECT  → 400. Impossible to offer here: this list is already one
 *     project's, from `GET /projects/:id/sprints`. Stated so nobody "helpfully" widens the source.
 *   · a CLOSED target              → 409 "pick an open sprint or the backlog"
 *   · THE SPRINT ITSELF            → 400 "a sprint cannot carry its unfinished tickets over to itself"
 *
 * ⭐ The remaining `active` one is the sprint being closed, which is excluded by the second rule — so
 * in practice this is the project's PLANNED sprints. It is not written as `state === 'planned'`
 * though: that would be a client-side re-derivation of the API's rule, and it would silently drop a
 * legal target the day a fourth state appears. ⛔ Exclude what is refused; do not enumerate what is
 * allowed.
 */
export function carryOverTargets(
  sprints: readonly Sprint[],
  closingSprintId: string,
): Sprint[] {
  return sprints.filter((sprint) => sprint.id !== closingSprintId && sprint.state !== 'closed');
}

/**
 * ⭐ `null` — the backlog — is ALWAYS a legal destination and is the default, because the API made it
 * the case that needs no body at all: *"Close it and put the leftovers back is the common case, so it
 * must be the case that needs no body."* ⛔ And there is no "leave them in the closed sprint" option
 * to offer, because the API has none: unfinished work in a closed sprint is invisible in every
 * backlog, which is the state the endpoint exists to prevent.
 */
export const CARRY_OVER_TO_BACKLOG = '';

// ── How many tickets are about to move ────────────────────────────────────────────────────────────

/**
 * How much we are entitled to claim about the number of tickets that will move.
 *
 *   exact   — this is the number.
 *   floor   — AT LEAST this many. We counted rows we hold, and we do not hold all of them.
 *   unknown — we cannot say. ⛔ Not "0", which is a different and much more dangerous claim to put
 *             in front of somebody about to press an irreversible button.
 */
export type CarryOverScope = 'exact' | 'floor' | 'unknown';

export interface CarryOverPreview {
  count: number | null;
  scope: CarryOverScope;
}

export const UNKNOWN_CARRY_OVER: CarryOverPreview = { count: null, scope: 'unknown' };

/**
 * How many of this sprint's tickets the API will move, as far as this client can honestly tell.
 *
 * 🔴 THE DEFINITION OF "UNFINISHED" IS THE SCHEMA'S, NOT OURS. The API moves exactly
 * `NOT status_def.is_final`, evaluated in SQL, and its own comment says why a hard-coded status list
 * there would go stale SILENTLY. ⇒ We read `is_final` off the loaded vocabulary for the same reason,
 * and when the vocabulary is NOT loaded we return `unknown` rather than guessing — a guess here is a
 * number a user consents to.
 *
 * ⚠️ THE THREE WAYS THIS BECOMES A FLOOR, all of them real on this screen:
 *   1. a status/assignee FILTER is on — the rows we hold are the matching ones, and a ticket hidden
 *      by the filter still moves;
 *   2. the section holds fewer rows than the server counted — the 100-row page cap;
 *   3. both.
 * ⛔ Printing a filtered count as though it were the whole sprint is the "confident wrong number"
 * this codebase refuses everywhere else — and here it would understate the consequence of a
 * destructive action, which is the worst direction to be wrong in.
 */
export function carryOverPreview(
  page: { items: readonly TicketListItem[]; total: number } | null,
  filtered: boolean,
  statuses: readonly StatusDef[],
): CarryOverPreview {
  // We have not loaded this sprint's rows (a collapsed section costs no request — by design).
  if (!page) return UNKNOWN_CARRY_OVER;
  // Without the vocabulary we cannot tell a final status from an unfinished one.
  if (statuses.length === 0) return UNKNOWN_CARRY_OVER;

  const finalCodes = new Set(statuses.filter((status) => status.is_final).map((s) => s.code));
  /**
   * ⚠️ An UNRECOGNISED status counts as unfinished. A code we cannot resolve is one the vocabulary
   * did not carry, and the two ways to be wrong are not symmetric: counting it as final UNDERSTATES
   * what is about to move, and understating the consequence is the failure this dialog exists to
   * prevent.
   */
  const count = page.items.filter((item) => !finalCodes.has(item.status)).length;

  const incomplete = filtered || page.items.length < page.total;
  return { count, scope: incomplete ? 'floor' : 'exact' };
}

/**
 * The consequence, in a sentence, for the confirmation dialog.
 *
 * 🔴 THE DEFECT PH-13 NAMES IS NOT THE MISSING BUTTON — it is a destructive, irreversible action with
 * no statement of what it does to the open items. So this string is the feature, and it must never
 * degrade into "Are you sure?".
 *
 * ⭐ The RULE is stated even when the NUMBER is unknown: "every ticket that is not in a final status
 * moves" is true regardless of what we managed to load, and it is the part that tells a reader what
 * will happen to work they cannot see on screen.
 */
export function describeCarryOver(preview: CarryOverPreview, targetName: string): string {
  const destination = `moved to ${targetName}`;

  if (preview.scope === 'unknown' || preview.count === null) {
    return `Every ticket in this sprint that is not in a final status will be ${destination}. We cannot say how many that is — this sprint's rows are not loaded.`;
  }

  if (preview.count === 0 && preview.scope === 'exact') {
    return `Nothing is unfinished, so no ticket will move. The sprint's numbers are snapshotted as they stand.`;
  }

  const noun = preview.count === 1 && preview.scope === 'exact' ? 'ticket' : 'tickets';

  if (preview.scope === 'floor') {
    return `At least ${preview.count} unfinished ${noun} will be ${destination} — and possibly more: this is counted from the rows currently loaded, not from the whole sprint.`;
  }

  return `${preview.count} unfinished ${noun} will be ${destination}.`;
}

/**
 * What the API actually did, once it has answered.
 *
 * ⭐ Its `carry_over` block is the TRUTH the preview above could only estimate — so the result
 * message is built from the server's numbers, never from ours. ⛔ Reusing the estimate here would let
 * a floor ("at least 3") stand as the record of what happened.
 */
export function describeCarryOverResult(result: {
  carry_over: { target_sprint_name: string | null; moved_count: number };
}): string {
  const { moved_count: moved, target_sprint_name: name } = result.carry_over;
  const where = name ?? 'the Backlog';
  if (moved === 0) return 'Sprint completed. Nothing was unfinished, so no ticket moved.';
  return `Sprint completed. ${moved} unfinished ticket${moved === 1 ? '' : 's'} moved to ${where}.`;
}
